import { TreeStructureData } from 'components/contentService/types'
import { NodeType } from 'shared/types/ingestion'
import {
  LessonEntry,
  UnitDefinition,
  LessonCentricData,
  SpreadsheetImportData,
  LessonAssociation,
} from 'domains/scope-and-sequence/types'
import {
  TreeCreateUnit,
  UnitContent,
  LessonRef,
  LessonGroup,
} from 'domains/scope-and-sequence/types'
import { rowKey } from './utility'

/**
 * Convert the API response to the lesson-centric format.
 * Lessons are already flat and unique. Suggested units are computed
 * by matching lesson standards against unit standards.
 */
export function convertApiResponseToLessonCentric(
  apiData: TreeStructureData,
): LessonCentricData {
  // Build standard → unit numbers map for computing suggestedUnits
  const standardToUnits = new Map<string, number[]>()
  apiData.units.forEach((unit) => {
    unit.standards.forEach((std) => {
      if (!standardToUnits.has(std)) standardToUnits.set(std, [])
      standardToUnits.get(std)!.push(unit.number)
    })
  })

  const lessons: LessonEntry[] = apiData.lessons.map((lesson) => {
    const key = rowKey(lesson)
    const suggestedUnitSet = new Set<number>()
    lesson.standards.forEach((std) => {
      standardToUnits.get(std)?.forEach((n) => suggestedUnitSet.add(n))
    })

    return {
      key,
      name: lesson.name,
      tree_id: lesson.tree_id,
      version_id: lesson.version_id,
      node_id: lesson.node_id,
      node_path: lesson.node_path,
      standards: lesson.standards,
      treeName:
        apiData.trees[[lesson.tree_id, lesson.version_id].join(':')] ?? '',
      suggestedUnits: Array.from(suggestedUnitSet).sort((a, b) => a - b),
    }
  })

  const units: UnitDefinition[] = apiData.units.map((u) => ({
    number: u.number,
    name: u.name,
  }))

  return {
    grade: apiData.grade,
    lessons,
    units,
    errors: apiData.errors,
  }
}

/**
 * Convert spreadsheet import data to the lesson-centric format.
 * Replaces convertToTreeStructureData(), convertToLessonPoolData(),
 * convertSpreadsheetLessonToTreeLesson(), and getLessonKey().
 */
export function convertSpreadsheetToLessonCentric(
  data: SpreadsheetImportData,
): LessonCentricData {
  const lessonMap = new Map<string, LessonEntry>()

  data.lessons.forEach((lesson) => {
    const key = `${lesson.treeId}:${lesson.versionId}:${lesson.nodeId}`
    const existing = lessonMap.get(key)

    // Parse suggested units from the comma-separated string
    const suggestedFromRow = lesson.suggestedUnits
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n))

    if (existing) {
      // Merge suggested units (deduplicate)
      suggestedFromRow.forEach((u) => {
        if (!existing.suggestedUnits.includes(u)) {
          existing.suggestedUnits.push(u)
        }
      })
    } else {
      lessonMap.set(key, {
        key,
        name: lesson.lessonName,
        tree_id: lesson.treeId,
        version_id: lesson.versionId,
        node_id: lesson.nodeId,
        node_path: lesson.nodePath.split(' > ').map((title, index) => ({
          index,
          type: NodeType.NodeList,
          title,
        })),
        standards: lesson.standards
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        treeName: '',
        suggestedUnits: suggestedFromRow,
      })
    }
  })

  const units: UnitDefinition[] = data.units.map((u) => ({
    number: u.number,
    name: u.name,
  }))

  return {
    grade: data.grade,
    lessons: Array.from(lessonMap.values()),
    units,
    errors: [],
  }
}

/**
 * Build the payload for the createTree API from lesson-centric data.
 * Groups lessons by assigned unit, builds sections/ordering,
 * produces TreeCreateUnit[] for the API.
 */
export function buildCreateTreePayload(
  data: LessonCentricData,
  associations: Record<string, LessonAssociation>,
  title: string,
): { grade: number; units: TreeCreateUnit[]; title: string } {
  // Group lessons by their assigned unit number
  const unitLessonsMap = new Map<number, LessonEntry[]>()

  data.lessons.forEach((lesson) => {
    const assoc = associations[lesson.key]
    if (assoc?.targetUnit != null) {
      if (!unitLessonsMap.has(assoc.targetUnit)) {
        unitLessonsMap.set(assoc.targetUnit, [])
      }
      unitLessonsMap.get(assoc.targetUnit)!.push(lesson)
    }
  })

  // Build TreeCreateUnit[] — one per unit definition
  const units: TreeCreateUnit[] = data.units.map((unitDef) => {
    const lessons = unitLessonsMap.get(unitDef.number) || []
    const content: UnitContent[] = []
    const sectionMap = new Map<
      string,
      { lesson: LessonRef; order: number | null }[]
    >()
    const ungroupedLessons: { lesson: LessonRef; order: number | null }[] = []

    lessons.forEach((lesson) => {
      const assoc = associations[lesson.key]
      const lessonRef: LessonRef = {
        node_id: lesson.node_id,
        tree_id: lesson.tree_id,
        version_id: lesson.version_id,
      }
      const order = assoc?.targetLessonNumber ?? null

      if (assoc?.targetSection) {
        if (!sectionMap.has(assoc.targetSection)) {
          sectionMap.set(assoc.targetSection, [])
        }
        sectionMap.get(assoc.targetSection)!.push({ lesson: lessonRef, order })
      } else {
        ungroupedLessons.push({ lesson: lessonRef, order })
      }
    })

    const sortByOrder = (
      a: { order: number | null },
      b: { order: number | null },
    ) => {
      if (a.order === null && b.order === null) return 0
      if (a.order === null) return 1
      if (b.order === null) return -1
      return a.order - b.order
    }

    ungroupedLessons.sort(sortByOrder)
    ungroupedLessons.forEach(({ lesson }) => content.push(lesson))

    sectionMap.forEach((sectionLessons, sectionName) => {
      sectionLessons.sort(sortByOrder)
      const groupName = /^\d+$/.test(sectionName)
        ? `Section ${sectionName}`
        : sectionName
      const group: LessonGroup = {
        name: groupName,
        lessons: sectionLessons.map(({ lesson }) => lesson),
      }
      content.push(group)
    })

    return { name: unitDef.name, content }
  })

  return { grade: data.grade, units, title }
}
